import { WebDashboardTabImpactBlock } from "@coeff/api";
import React, { useState } from "react";
import { createPortal } from "react-dom";

import { ErrorCircleIcon } from "../../components";

interface DisableTabModalProps {
  tabName: string;
  affectedBlocks: WebDashboardTabImpactBlock[];
  /** Impact lookup in flight — the warning section shows a placeholder until it resolves. */
  isLoadingImpact: boolean;
  onCancel: () => void;
  onProceed: (deleteAffectedCharts: boolean) => void;
  /** Web dashboard renders inside an iframe; the modal must portal into that document. */
  portalDocument?: Document;
}

/**
 * Confirmation shown before disabling a source tab (ENG-7204). When charts depend on the
 * tab it lists them and offers to delete them along with the disable.
 */
export const DisableTabModal: React.FC<DisableTabModalProps> = ({
  tabName,
  affectedBlocks,
  isLoadingImpact,
  onCancel,
  onProceed,
  portalDocument,
}) => {
  const [deleteAffectedCharts, setDeleteAffectedCharts] = useState(false);

  const hasAffectedBlocks = affectedBlocks.length > 0;

  const modal = (
    <div
      data-source-sheet-disable-modal
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/45 p-5"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Disable Tab"
        onClick={e => e.stopPropagation()}
        className="flex w-full max-w-[480px] flex-col gap-4 rounded-lg bg-main-background p-5 text-main-foreground shadow-lg"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <span className="text-lg font-semibold leading-[22px]">Disable Tab</span>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-main-muted-foreground hover:bg-main-foreground/10"
          >
            <span className="material-icons !text-base">close</span>
          </button>
        </div>

        <p className="text-base font-normal leading-5">
          Are you sure you want to disable the &ldquo;
          <span className="font-semibold">{tabName}</span>
          &rdquo; tab?
        </p>

        {isLoadingImpact ? (
          <div className="text-sm text-main-muted-foreground">Checking affected charts…</div>
        ) : hasAffectedBlocks ? (
          <div className="flex w-full flex-col gap-2 rounded bg-[#fdf2dc] p-2.5 text-gray-900">
            <div className="flex items-start gap-1.5">
              <ErrorCircleIcon width={16} height={16} className="mt-px shrink-0 text-amber-500" />
              <span className="text-sm font-semibold leading-[18px]">
                Warning: {affectedBlocks.length}{" "}
                {affectedBlocks.length === 1 ? "chart depends" : "charts depend"} on this tab
              </span>
            </div>
            <ul className="max-h-40 list-disc overflow-y-auto pl-10 text-sm font-normal leading-[18px]">
              {affectedBlocks.map(block => (
                <li key={block.block_id}>{block.title}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {!isLoadingImpact && hasAffectedBlocks && (
          <label className="flex w-fit cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={deleteAffectedCharts}
              onChange={e => setDeleteAffectedCharts(e.target.checked)}
              className="size-4 accent-primary-button-background"
            />
            <span className="text-sm font-medium">Also delete affected charts</span>
          </label>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onCancel}
            className="h-8 cursor-pointer rounded px-3 text-sm font-medium text-primary-button-background hover:bg-primary-button-background/10"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isLoadingImpact}
            onClick={() => onProceed(deleteAffectedCharts)}
            className="h-8 cursor-pointer rounded bg-primary-button-background/10 px-3 text-sm font-medium text-primary-button-background hover:bg-primary-button-background/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, portalDocument?.body ?? document.body);
};
